import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { useSession, useLogout } from "../lib/auth";
import { ROLE_LABEL } from "../lib/permissions";
import { useUserPreferences } from "../lib/userPreferences";
import { SectionCard } from "../components/ui/SectionCard";
import { SegmentedControl } from "../components/ui/SegmentedControl";
import { Button } from "../components/ui/Button";

export default function Settings(): JSX.Element {
  const { t } = useTranslation(["settings", "common"]);
  const { user } = useSession();
  const [prefs, setPrefs] = useUserPreferences();
  const logout = useLogout();
  // Confirmation modal — keeps a single misclick from booting the user out.
  // Reversible (they can sign back in), but a one-tap confirm is the universal
  // pattern for destructive UI (Slack, Linear, macOS, iOS).
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <div className="max-w-3xl mx-auto">
      {/* Page header */}
      <div className="mb-[18px]">
        <h1 className="text-[24px] leading-[1.1] font-display font-semibold text-text">
          {t("page_title")}
        </h1>
        <p className="text-[13px] text-muted mt-[5px]">{t("page_subtitle")}</p>
      </div>

      <SectionCard title={t("page_title")} bodyClassName="p-0">
        {/* ── Profile subsection ──────────────────────────────────── */}
        <section aria-labelledby="settings-profile-heading" className="px-[15px] py-[9px]">
          <h2
            id="settings-profile-heading"
            className="text-[11px] font-bold tracking-[.05em] uppercase text-dim mb-[13px] pt-[5px]"
          >
            {t("profile_section")}
          </h2>
          <dl>
            {/* Name row */}
            <div className="flex items-center gap-[14px] py-[8px] border-b border-border-2">
              <dt className="w-[148px] shrink-0 text-[13.5px] text-label">
                {t("name_label")}
              </dt>
              <dd className="text-[14px] font-semibold text-text">
                {user?.name ?? <span className="text-dim font-normal">—</span>}
              </dd>
            </div>
            {/* Role row */}
            <div className="flex items-center gap-[14px] py-[8px]">
              <dt className="w-[148px] shrink-0 text-[13.5px] text-label">
                {t("role_label")}
              </dt>
              <dd>
                <span className="inline-flex items-center text-[11.5px] font-semibold px-[9px] py-[2px] rounded-full border bg-surface-2 text-muted border-border">
                  {user ? ROLE_LABEL[user.role] : "—"}
                </span>
              </dd>
            </div>
          </dl>
        </section>

        {/* ── Divider ─────────────────────────────────────────────── */}
        <div className="h-px bg-border-2 mx-[15px]" role="separator" aria-hidden="true" />

        {/* ── Preferences subsection ──────────────────────────────── */}
        <section aria-labelledby="settings-preferences-heading" className="px-[15px] py-[9px] pb-[13px]">
          <h2
            id="settings-preferences-heading"
            className="text-[11px] font-bold tracking-[.05em] uppercase text-dim mb-[13px] pt-[5px]"
          >
            {t("preferences_section")}
          </h2>

          <div className="space-y-3">
            {/* Compact rows toggle */}
            <label
              className="flex items-center justify-between gap-4 p-[11px_13px] rounded-[10px] border border-border-2 cursor-pointer hover:bg-surface-2 transition-colors min-h-[44px]"
            >
              <div>
                <p className="text-[13.5px] font-semibold text-text">{t("compact_rows_label")}</p>
                <p className="text-[12px] text-muted mt-0.5">{t("compact_rows_desc")}</p>
              </div>
              <input
                type="checkbox"
                checked={prefs.compactRows}
                onChange={(e) => setPrefs({ compactRows: e.target.checked })}
                className="h-5 w-5 rounded border-border accent-accent shrink-0"
                aria-label="Enable compact rows"
              />
            </label>

            {/* Language */}
            <div className="flex items-center justify-between gap-4 p-[11px_13px] rounded-[10px] border border-border-2 min-h-[44px]">
              <div>
                <p className="text-[13.5px] font-semibold text-text">{t("language_label")}</p>
                <p className="text-[12px] text-muted mt-0.5">{t("language_desc")}</p>
              </div>
              <SegmentedControl<"en" | "fr">
                options={[
                  { value: "en", label: "English" },
                  { value: "fr", label: "Français" },
                ]}
                value={prefs.language}
                onChange={(lang) => setPrefs({ language: lang })}
                label="Interface language"
              />
            </div>
          </div>

          <p className="text-[12px] text-dim mt-3">
            {t("language_coming_soon")}
          </p>
        </section>

        {/* ── Divider ─────────────────────────────────────────────── */}
        <div className="h-px bg-border-2 mx-[15px]" role="separator" aria-hidden="true" />

        {/* ── Account subsection ──────────────────────────────────── */}
        <section aria-labelledby="settings-account-heading" className="px-[15px] py-[9px] pb-[13px]">
          <h2
            id="settings-account-heading"
            className="text-[11px] font-bold tracking-[.05em] uppercase text-dim mb-[13px] pt-[5px]"
          >
            {t("account_section")}
          </h2>

          <div className="flex items-center justify-between gap-4 p-[11px_13px] rounded-[10px] border border-border-2 min-h-[44px]">
            <div>
              <p className="text-[13.5px] font-semibold text-text">{t("sign_out_label")}</p>
              <p className="text-[12px] text-muted mt-0.5">{t("sign_out_desc")}</p>
            </div>
            <Button
              variant="danger-soft"
              onClick={() => setConfirmOpen(true)}
              data-testid="settings-signout-button"
            >
              {t("common:sign_out")}
            </Button>
          </div>
        </section>
      </SectionCard>

      {/* Sign-out confirmation modal — Radix Dialog. Mirrors the project's
          existing confirm-dialog pattern (RevokeUserConfirmDialog,
          DeleteEntityDialog). One-tap confirm; the action is reversible
          so no type-to-confirm. */}
      <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
          <Dialog.Content
            data-testid="settings-signout-confirm-dialog"
            className="fixed left-1/2 top-1/2 z-50 w-[min(92vw,420px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-surface border border-border p-6 shadow-xl focus:outline-none"
          >
            <Dialog.Title className="text-[16px] font-semibold text-text mb-1">
              {t("sign_out_confirm_title")}
            </Dialog.Title>
            <Dialog.Description className="text-[13px] text-dim mb-4">
              {t("sign_out_confirm_body")}
            </Dialog.Description>
            <div className="mt-5 flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmOpen(false)}
                data-testid="settings-signout-cancel"
              >
                {t("common:cancel")}
              </Button>
              <Button
                type="button"
                variant="danger-soft"
                onClick={() => {
                  setConfirmOpen(false);
                  void logout();
                }}
                data-testid="settings-signout-confirm"
              >
                {t("common:sign_out")}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
